import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import {
  SPQueryKeys,
  SP_DO_NOT_USE_OPTIONS,
  getOnboardingForm,
} from "@/api/service-provider/serviceProvider.queries";
import type { ServiceProviderList } from "@/api/service-provider/serviceProvider.types";
import { TradeCell } from "@/components/features/ServiceProviders/TradeCell";
import BulkImportModal from "@/components/ui/BulkImport/BulkImportModal";
import { spImportDescriptor } from "@/components/features/ServiceProviders/BulkUpload/spImportDescriptor";
import { DoNotUseBadge } from "@/components/features/ServiceProviders/DoNotUse/DoNotUseBadge";
import { DO_NOT_USE_LABEL } from "@/components/features/ServiceProviders/DoNotUse/doNotUse.constants";
import { Badge, Table } from "@onengine/component-library";
import type { TableColumn } from "@onengine/component-library";
import { getServiceProviderStatusBadgeColors } from "@/utils/badgeConfig";
import { Button } from "@/components/ui/Button/Button";
import { handleApiError } from "@/utils/responseHandler";
import type { ApiError } from "@/types/common.types";
import { Menu } from "@/components/ui/Menu";
import Loader from "@/components/ui/Loader/Loader";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useMergedView } from "@/hooks/usePlatformView";
import {
  useProviderTypeOptions,
  useServiceProviderList,
} from "@/hooks/service-provider/useServiceProviderQueries";
import { useServiceProviderOnboardingStore } from "@/store/useServiceProviderOnboardingStore";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { Flex, Group, Modal, Stack, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconPlus, IconUpload } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { tableSearchSchema } from "@/hooks/useTableUrlPersistence";
import {
  InlineTableCell,
  renderBadgeCell,
  renderTextCell,
  renderTextSubtextCell,
} from "@/components/table/tableCells";
import { useServerTable } from "@/hooks/table/useServerTable";
import type { TableFilterColumnConfig } from "@/hooks/table/useTableFilterOptions";
import { useEffect, useMemo, useState } from "react";

export const Route = createFileRoute("/_authenticated/service-providers/")({
  component: ServiceProviders,
  validateSearch: tableSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const VIEW_NAME = "serviceProviderList";

type ServiceProviderType = {
  label: string;
  value: string;
};

const formatProviderTypeLabel = (value: string): string => {
  if (!value) {
    return "";
  }

  return value
    .replace(/[-_]/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const COMPANY_MENU_VALUE = "__company__";
const COMPANY_SUBTYPES = ["mom_pop", "owner_operator", "vendor"] as const;

const TOP_LEVEL_TYPE_LABELS: Record<string, string> = {
  independent_tech: "Independent Technician",
  [COMPANY_MENU_VALUE]: "Company",
  w2: "W-2",
};

const COMPANY_SUBTYPE_LABELS: Record<string, string> = {
  mom_pop: "Mom & Pop",
  owner_operator: "Owner Operator",
  vendor: "Vendor",
};

const SP_FILTER_COLUMNS: TableFilterColumnConfig[] = [
  {
    columnId: "name",
    filterByKey: "name",
    storeType: "serviceProviders",
    valueSource: "label",
    searchParams: { type: "Users", fields: ["value"], limit: 10, key: "name" },
  },
  {
    columnId: "providerType",
    filterByKey: "type",
    storeType: "serviceProviders",
    valueSource: "label",
    searchParams: { type: "LookupTypes", fields: ["label"], limit: 10, key: "type" },
  },
  {
    columnId: "trade",
    filterByKey: "trades",
    storeType: "serviceProviders",
    valueSource: "label",
    searchParams: { type: "Trades", fields: ["name"], limit: 100, key: "trades" },
  },
  {
    columnId: "status",
    filterByKey: "status",
    storeType: "serviceProviders",
    valueSource: "label",
    searchParams: { type: "status", fields: ["label"], limit: 20, key: "status" },
  },
  {
    columnId: "profileStage",
    filterByKey: "stage",
    storeType: "serviceProviders",
    valueSource: "label",
    searchParams: { type: "LookupTypes", fields: ["label"], limit: 20, key: "stage" },
  },
  {
    columnId: "address",
    filterByKey: "address",
    storeType: "serviceProviders",
    valueSource: "label",
    searchParams: { type: "GeographyCities", fields: ["city"], limit: 10, key: "location" },
  },
  {
    columnId: "doNotUse",
    filterByKey: "doNotUse",
    staticItems: SP_DO_NOT_USE_OPTIONS.map((option) => ({ id: option.value, label: option.label })),
  },
];

const getSortByField = (columnId: string): string | undefined => {
  const fieldMap: Record<string, string> = {
    name: "name",
    providerType: "type",
    status: "status",
    profileStage: "stage",
    trade: "trades",
    address: "address",
    primaryEmail: "email",
    doNotUse: "doNotUse",
  };
  return fieldMap[columnId];
};

function ServiceProviders() {
  const searchParams = useSearch({ from: "/_authenticated/service-providers/" });

  useBreadcrumb(
    [
      {
        title: "Service Providers",
        href: "/service-providers",
        icon: getRouteIcon(MODULE_KEYS_MAP.serviceProviders, 16),
      },
    ],
    { listView: "List View" },
  );

  const queryClient = useQueryClient();
  const { setProviderTypeId, reset, clearServiceProviderId } = useServiceProviderOnboardingStore();
  const { data: providerTypeOptions, isLoading: isProviderTypeLoading } = useProviderTypeOptions();

  // Clear serviceProviderId when user navigates to /service-providers
  // This ensures it's cleared after completing onboarding
  useEffect(() => {
    clearServiceProviderId();
  }, [clearServiceProviderId]);

  const { hasAccess } = usePermissions();

  const { create: canCreateServiceProvider } = hasAccess({
    module: MODULE_KEYS_MAP.serviceProviders,
  });
  const mergedView = useMergedView();

  const { navigate, queryOptions, isPrefsLoading, controlProps, tableKey } = useServerTable({
    viewName: VIEW_NAME,
    columns: SP_FILTER_COLUMNS,
    getSortByField,
    defaultStoreType: "serviceProviders",
    searchParams,
  });

  const {
    data: serviceProviderData,
    isFetching,
    isLoading,
    isPlaceholderData,
  } = useServiceProviderList(queryOptions);

  // Show loading overlay on:
  // 1. Initial load (isLoading)
  // 2. Pagination/filter changes when showing placeholder data from previous query
  const showLoadingOverlay = isLoading || (isFetching && isPlaceholderData) || isPrefsLoading;

  const tableData = useMemo<ServiceProviderList[]>(
    () => serviceProviderData?.data ?? [],
    [serviceProviderData],
  );

  const [companyTypeModalOpen, { open: openCompanyTypeModal, close: closeCompanyTypeModal }] =
    useDisclosure(false);

  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const openBulkUploadModal = () => setBulkUploadModalOpen(true);

  const companySubOptions = useMemo<ServiceProviderType[]>(() => {
    if (!providerTypeOptions) {
      return [];
    }

    return COMPANY_SUBTYPES.filter((subtype) =>
      providerTypeOptions.some((option) => option.value === subtype),
    ).map((subtype) => ({
      value: subtype,
      label: COMPANY_SUBTYPE_LABELS[subtype],
    }));
  }, [providerTypeOptions]);

  const clientOptions = useMemo<ServiceProviderType[]>(() => {
    if (!providerTypeOptions) {
      return [];
    }

    const topLevel: ServiceProviderType[] = [];

    if (providerTypeOptions.some((option) => option.value === "independent_tech")) {
      topLevel.push({
        value: "independent_tech",
        label: TOP_LEVEL_TYPE_LABELS.independent_tech,
      });
    }
    if (companySubOptions.length > 0) {
      topLevel.push({
        value: COMPANY_MENU_VALUE,
        label: TOP_LEVEL_TYPE_LABELS[COMPANY_MENU_VALUE],
      });
    }
    if (providerTypeOptions.some((option) => option.value === "w2")) {
      topLevel.push({ value: "w2", label: TOP_LEVEL_TYPE_LABELS.w2 });
    }

    if (topLevel.length > 0) {
      return topLevel;
    }

    return providerTypeOptions.map((option) => ({
      value: option.value,
      label: formatProviderTypeLabel(option.value),
    }));
  }, [providerTypeOptions, companySubOptions]);

  const handleNewServiceProviderClick = async (value: string) => {
    if (value === COMPANY_MENU_VALUE) {
      openCompanyTypeModal();
      return;
    }

    closeCompanyTypeModal();
    const selectedOption = providerTypeOptions?.find((option) => option.value === value);
    const providerTypeId = selectedOption ? String(selectedOption.id) : undefined;
    reset();
    setProviderTypeId(providerTypeId ?? null);

    let targetStepKey = "personal-info";

    try {
      const form = await queryClient.fetchQuery({
        queryKey: [SPQueryKeys.spForm, value],
        queryFn: () => getOnboardingForm(value),
      });

      const firstStep = form?.steps?.find((step) => step.stepNumber === 1) ?? form?.steps?.[0];

      if (firstStep?.stepKey) {
        targetStepKey = firstStep.stepKey;
      }
    } catch (error) {
      handleApiError(error as ApiError);
    }

    navigate({
      to: `/service-providers/onboarding/$providerType/$stepId`,
      params: { providerType: value, stepId: targetStepKey },
    });
  };

  const columns = useMemo<TableColumn<ServiceProviderList>[]>(
    () => [
      {
        key: "name",
        label: "Name",
        sortable: true,
        filterable: true,
        filterVariant: "list",
        filterSelectionMode: "multi",
        render: (row) => renderTextSubtextCell(row.name, row.address || "—"),
      },
      {
        key: "providerType",
        label: "Type",
        sortable: true,
        filterable: true,
        filterVariant: "list",
        filterSelectionMode: "multi",
        render: (row) => renderTextCell(row.providerType || "—"),
      },
      {
        id: "trade",
        key: "trades",
        label: "Trade",
        sortable: true,
        filterable: true,
        filterVariant: "list",
        filterSelectionMode: "multi",
        render: (row) => (
          <InlineTableCell>
            <TradeCell trade={row.trades} subtrades={row.subTrades} />
          </InlineTableCell>
        ),
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        filterable: true,
        filterVariant: "list",
        filterSelectionMode: "multi",
        render: (row) =>
          row.status
            ? renderBadgeCell(
                <Badge {...getServiceProviderStatusBadgeColors(row.status)} size="small">
                  {row.status}
                </Badge>,
              )
            : renderTextCell("—"),
      },
      {
        key: "profileStage",
        label: "Stage",
        sortable: true,
        filterable: true,
        filterVariant: "list",
        filterSelectionMode: "multi",
        render: (row) => renderTextCell(row.profileStage || "—"),
      },
      {
        key: "address",
        label: "Location",
        sortable: true,
        filterable: true,
        filterVariant: "list",
        filterSelectionMode: "multi",
        render: (row) => renderTextCell(row.address || "—"),
      },
      {
        // Not filterable, unlike every sibling column: the filter panel drives
        // each one off a typeahead lookup source and there is none for emails.
        // Email is reachable through the search box instead (SERV-2717).
        key: "primaryEmail",
        label: "Email",
        sortable: true,
        render: (row) => renderTextCell(row.primaryEmail || "—"),
      },
      {
        key: "doNotUse",
        label: DO_NOT_USE_LABEL,
        sortable: true,
        filterable: true,
        filterVariant: "list",
        filterSelectionMode: "multi",
        render: (row) =>
          row.doNotUse === true
            ? renderBadgeCell(<DoNotUseBadge size="small" />)
            : renderTextCell("—"),
      },
    ],
    [],
  );

  return (
    <Flex
      direction="column"
      style={{
        margin: "calc(var(--mantine-spacing-md) * -1)", // To offset padding
        padding: "var(--spacing-medium)",
        backgroundColor: "var(--neutral-500)",
      }}
    >
      <Group
        justify="space-between"
        align="center"
        style={{ marginBottom: "var(--spacing-medium)" }}
      >
        <Title
          order={3}
          style={{
            fontWeight: "var(--font-weight-bold)",
            fontSize: "var(--heading-font-size-h3)",
          }}
        >
          Service Providers
        </Title>
        {canCreateServiceProvider && !mergedView ? (
          <Group gap="var(--spacing-small)">
            <Button
              variant="secondary"
              style={{ height: "var(----component-size-small)" }}
              iconLeft={<IconUpload size={16} />}
              onClick={openBulkUploadModal}
            >
              Bulk Upload
            </Button>
            <Menu
              items={clientOptions}
              onSelect={handleNewServiceProviderClick}
              trigger={
                <Button
                  variant="primary"
                  style={{ height: "var(----component-size-small)" }}
                  iconLeft={isProviderTypeLoading ? undefined : <IconPlus size={16} />}
                  disabled={
                    !canCreateServiceProvider || isProviderTypeLoading || clientOptions.length === 0
                  }
                >
                  {isProviderTypeLoading ? (
                    <Flex align="center" gap="xs">
                      <Loader size="small" withContainer={false} />
                      Loading...
                    </Flex>
                  ) : (
                    "New Service Provider"
                  )}
                </Button>
              }
            />
          </Group>
        ) : undefined}
      </Group>
      <BulkImportModal
        descriptor={spImportDescriptor}
        ctx={undefined}
        opened={bulkUploadModalOpen}
        onClose={() => setBulkUploadModalOpen(false)}
      />
      <Modal
        opened={companyTypeModalOpen}
        onClose={closeCompanyTypeModal}
        title={
          <Text fw={700} size="lg">
            Select Company Type
          </Text>
        }
        size="sm"
        centered
      >
        <Stack gap="var(--spacing-medium)">
          <Text size="sm" c="var(--text-secondary)">
            Choose the type of company for this service provider.
          </Text>
          {companySubOptions.map((option) => (
            <Button
              key={option.value}
              variant="neutral-secondary"
              onClick={() => handleNewServiceProviderClick(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </Stack>
      </Modal>
      <div>
        <Table<ServiceProviderList>
          key={tableKey}
          {...controlProps}
          columns={columns}
          data={tableData}
          loading={showLoadingOverlay}
          totalCount={serviceProviderData?.count ?? 0}
          onRowClick={(row) =>
            navigate({
              to: "/service-providers/$id",
              params: { id: String(row.id) },
            })
          }
          emptyText="No service providers found"
        />
      </div>
    </Flex>
  );
}
